/* /portaal/berichten — mobile chat per kind met de mentor/leidster. */
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";

export default function PortaalBerichtenPage() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [selKid, setSelKid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    (async () => {
      try {
        const ov = await api.get("/portaal/overview");
        const list = ov.data.kinderen || [];
        setKids(list);
        if (list.length) setSelKid(list[0]);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    })();
  }, [status, user]);

  useEffect(() => {
    if (!selKid) return;
    (async () => {
      try {
        const r = await api.get(`/chat/${selKid.id}`);
        setMessages(r.data?.messages || []);
        setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    })();
  }, [selKid]);

  const send = async () => {
    if (!body.trim() || !selKid) return;
    setSending(true);
    try {
      const r = await api.post("/mentorbericht", { kind_id: selKid.id, body: body.trim(), type: "text" });
      setMessages(m => [...m, r.data]);
      setBody("");
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setSending(false); }
  };

  if (status === "loading") return null;
  if (status !== "authed" || user?.role !== "ouder") return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col pb-24" data-testid="portaal-berichten-page">
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portaal")} data-testid="berichten-back"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="text-base font-semibold text-neutral-900">Berichten</div>
          <div className="text-xs text-neutral-500">{selKid ? `Chat met leidsters van ${selKid.voornaam}` : "Chat met de leidster"}</div>
        </div>
      </div>

      {kids.length > 1 && (
        <div className="px-4 pt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {kids.map(k => (
            <Button
              key={k.id}
              size="sm"
              variant={selKid?.id === k.id ? "default" : "outline"}
              onClick={() => setSelKid(k)}
              className={selKid?.id === k.id ? "text-white shrink-0" : "shrink-0"}
              style={selKid?.id === k.id ? { background: "var(--sage)" } : {}}
              data-testid={`berichten-kid-${k.id}`}
            >{k.voornaam}</Button>
          ))}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="berichten-thread">
        {messages.length === 0 && (
          <Card className="p-6 bg-white text-center">
            <MessageSquare className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
            <div className="text-sm text-neutral-600">Nog geen berichten. Stuur de eerste!</div>
          </Card>
        )}
        {messages.map((m, i) => {
          const isMine = m.van_type === "ouder";
          return (
            <div key={m.id || i} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`bericht-${m.id || i}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-2xl ${isMine ? "bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-br-sm" : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-sm"}`}>
                {!isMine && m.van_naam && <div className="text-[11px] font-medium text-neutral-500 mb-0.5">{m.van_naam}</div>}
                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                <div className="text-[10px] text-neutral-400 mt-1">{(m.created_at || "").slice(11, 16)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="sticky bottom-16 lg:bottom-0 z-10 bg-white border-t border-neutral-200 px-3 py-3 flex items-end gap-2">
        <Textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Bericht typen…"
          className="min-h-[44px] max-h-40 resize-none flex-1"
          data-testid="berichten-input"
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <Button onClick={send} disabled={sending || !body.trim()} className="text-white h-11 w-11 p-0 shrink-0" style={{ background: "var(--sage)" }} data-testid="berichten-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
